import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Check,
  Clock3,
  Copy,
  Globe2,
  Info,
  Plus,
  Save,
  Trash2
} from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Select } from "@/components/ui/select.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { PageHeader } from "@/components/ui/page-header.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card.js";
import { useToast } from "@/components/ui/toast.js";
import { cn } from "@/lib/cn.js";
import type { BulkScheduleOverridesInput, Resource, Schedule } from "@/lib/types.js";

type Interval = [string, string];
type DayKey = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

const DAYS: ReadonlyArray<{ key: DayKey; label: string; short: string; weekday: number }> = [
  { key: "monday", label: "Monday", short: "Mon", weekday: 1 },
  { key: "tuesday", label: "Tuesday", short: "Tue", weekday: 2 },
  { key: "wednesday", label: "Wednesday", short: "Wed", weekday: 3 },
  { key: "thursday", label: "Thursday", short: "Thu", weekday: 4 },
  { key: "friday", label: "Friday", short: "Fri", weekday: 5 },
  { key: "saturday", label: "Saturday", short: "Sat", weekday: 6 },
  { key: "sunday", label: "Sunday", short: "Sun", weekday: 0 }
];

const DEFAULT_WINDOW: Interval = ["09:00", "17:00"];

export function SchedulesPage(): React.JSX.Element {
  const { api, me } = useTenant();
  const resources = useAsync(() => api.listResources(), [api]);
  const visibleResources = (resources.data?.data ?? []).filter((resource) => !resource.deleted_at);
  const [resourceId, setResourceId] = useState("");

  useEffect(() => {
    if (!resourceId && visibleResources[0]) setResourceId(visibleResources[0].id);
    if (resourceId && !visibleResources.some((resource) => resource.id === resourceId)) {
      setResourceId(visibleResources[0]?.id ?? "");
    }
  }, [resourceId, visibleResources]);

  const selectedResource = visibleResources.find((resource) => resource.id === resourceId);
  const timezone = me?.tenant.primary_tz ?? "Local time";

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow="Schedule"
        title="Availability"
        description="Set recurring working hours, publish bookable windows for a date range, and block personal time off. Website slots update from this schedule immediately."
        actions={
          <div className="min-w-[260px]">
            <Label htmlFor="schedule-resource" className="sr-only">Bookable resource</Label>
            <Select
              id="schedule-resource"
              value={resourceId}
              onChange={(event) => setResourceId(event.target.value)}
              aria-label="Bookable resource"
            >
              <option value="">Select a doctor or resource</option>
              {visibleResources.map((resource) => (
                <option key={resource.id} value={resource.id}>{resource.name}</option>
              ))}
            </Select>
          </div>
        }
      />

      {resources.loading ? (
        <Skeleton className="h-80 w-full" />
      ) : visibleResources.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex min-h-48 flex-col items-center justify-center gap-2 text-center">
            <CalendarDays className="h-7 w-7 text-muted-foreground" />
            <p className="font-medium">No bookable resources yet</p>
            <p className="max-w-md text-sm text-muted-foreground">Add a doctor, staff member, room, or other resource before publishing availability.</p>
          </CardContent>
        </Card>
      ) : selectedResource ? (
        <ScheduleEditor key={selectedResource.id} resource={selectedResource} timezone={timezone} />
      ) : null}
    </div>
  );
}

function ScheduleEditor({ resource, timezone }: { resource: Resource; timezone: string }): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const schedule = useAsync(() => api.getSchedule(resource.id), [api, resource.id]);
  const [draft, setDraft] = useState<Schedule | null>(null);
  const [saving, setSaving] = useState(false);
  const [restoringDate, setRestoringDate] = useState<string | null>(null);

  useEffect(() => {
    if (schedule.data) setDraft(normalizeSchedule(schedule.data));
  }, [schedule.data]);

  const stats = useMemo(() => scheduleStats(draft), [draft]);

  if (schedule.loading && !draft) return <Skeleton className="h-[640px] w-full" />;
  if (schedule.error && !draft) {
    return (
      <Card className="border-destructive/30">
        <CardContent className="flex min-h-40 flex-col items-center justify-center gap-3 text-center">
          <p className="font-medium">Could not load this schedule</p>
          <p className="text-sm text-muted-foreground">{schedule.error.message}</p>
          <Button variant="outline" onClick={schedule.reload}>Try again</Button>
        </CardContent>
      </Card>
    );
  }
  if (!draft) return <Skeleton className="h-[640px] w-full" />;

  function setDay(day: DayKey, intervals: Interval[]): void {
    setDraft((current) => current ? {
      ...current,
      weekly_hours: { ...current.weekly_hours, [day]: intervals }
    } : current);
  }

  function copyMondayToWeekdays(): void {
    const monday = draft?.weekly_hours.monday ?? [];
    setDraft((current) => {
      if (!current) return current;
      const weekly = { ...current.weekly_hours };
      for (const day of DAYS.slice(0, 5)) weekly[day.key] = monday.map((interval) => [...interval] as Interval);
      return { ...current, weekly_hours: weekly };
    });
  }

  function addTimeOff(): void {
    const today = localDateKey(new Date());
    const tomorrow = addDateKey(today, 1);
    setDraft((current) => current ? {
      ...current,
      time_off: [
        ...current.time_off,
        {
          starts_at: localDateTimeToIso(`${today}T00:00`),
          ends_at: localDateTimeToIso(`${tomorrow}T00:00`),
          reason: ""
        }
      ]
    } : current);
  }

  function patchTimeOff(index: number, patch: Partial<Schedule["time_off"][number]>): void {
    setDraft((current) => current ? {
      ...current,
      time_off: current.time_off.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row)
    } : current);
  }

  async function saveSchedule(): Promise<void> {
    if (!draft) return;
    const validation = validateScheduleDraft(draft);
    if (validation) {
      toast({ title: "Check the schedule", description: validation, variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const saved = await api.putSchedule(resource.id, draft);
      setDraft(normalizeSchedule(saved));
      toast({ title: "Availability saved", description: "New website searches now use these hours.", variant: "success" });
    } catch (error) {
      toast({ title: "Could not save availability", description: errorMessage(error), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function publishRange(input: BulkScheduleOverridesInput, dates: string[]): Promise<boolean> {
    try {
      const result = await api.bulkScheduleOverrides(resource.id, input);
      setDraft((current) => {
        if (!current) return current;
        const overrides = { ...current.overrides };
        for (const date of dates) overrides[date] = input.closed ? [] : (input.hours ?? []).map((interval) => [...interval] as Interval);
        return { ...current, overrides };
      });
      toast({
        title: input.closed ? "Dates closed" : "Availability published",
        description: `${result.dates_written} ${result.dates_written === 1 ? "day" : "days"} updated for ${resource.name}.`,
        variant: "success"
      });
      return true;
    } catch (error) {
      toast({ title: "Could not publish date range", description: errorMessage(error), variant: "destructive" });
      return false;
    }
  }

  async function restoreWeeklyHours(date: string): Promise<void> {
    if (!draft) return;
    const nextOverrides = { ...draft.overrides };
    delete nextOverrides[date];
    const next = { ...draft, overrides: nextOverrides };
    setRestoringDate(date);
    try {
      const saved = await api.putSchedule(resource.id, next);
      setDraft(normalizeSchedule(saved));
      toast({ title: "Weekly hours restored", description: `${formatDateLabel(date)} now follows the recurring schedule.`, variant: "success" });
    } catch (error) {
      toast({ title: "Could not remove override", description: errorMessage(error), variant: "destructive" });
    } finally {
      setRestoringDate(null);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard icon={<CalendarDays className="h-4 w-4" />} label="Recurring week" value={`${stats.openDays} open days`} detail={`${stats.weeklyHours.toFixed(stats.weeklyHours % 1 ? 1 : 0)} scheduled hours`} />
        <SummaryCard icon={<Clock3 className="h-4 w-4" />} label="Date overrides" value={`${stats.overrideCount} published`} detail={stats.nextOverride ? `Next: ${formatDateLabel(stats.nextOverride)}` : "No exceptional dates"} />
        <SummaryCard icon={<Globe2 className="h-4 w-4" />} label="Website timezone" value={timezone} detail="Availability is calculated in clinic time" />
      </div>

      {resource.crm_employee_id && (
        <div className="flex gap-2 rounded-lg border border-info/20 bg-info/5 px-3 py-2.5 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" />
          <p><span className="font-medium text-foreground">CRM-linked resource.</span> Identity is managed in CRM. If schedule writes are locked for this resource, update its calendar pack in CRM so the next sync does not overwrite the hours.</p>
        </div>
      )}

      <Card>
        <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Recurring weekly hours</CardTitle>
            <CardDescription className="mt-1">The default pattern used unless a date override or time-off block applies.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={copyMondayToWeekdays}>
            <Copy className="h-3.5 w-3.5" /> Copy Monday to weekdays
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {DAYS.map((day) => {
              const intervals = draft.weekly_hours[day.key] ?? [];
              const open = intervals.length > 0;
              return (
                <div key={day.key} className="grid gap-3 px-5 py-3.5 md:grid-cols-[116px_105px_minmax(0,1fr)] md:items-center">
                  <div className="font-medium">{day.label}</div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={open}
                    onClick={() => setDay(day.key, open ? [] : [[...DEFAULT_WINDOW] as Interval])}
                    className={cn(
                      "focus-ring inline-flex h-8 w-[94px] items-center justify-between rounded-full border px-2.5 text-xs font-medium transition-colors",
                      open ? "border-success/25 bg-success/10 text-success" : "border-border bg-muted/50 text-muted-foreground"
                    )}
                  >
                    <span>{open ? "Open" : "Closed"}</span>
                    <span className={cn("h-2 w-2 rounded-full", open ? "bg-success" : "bg-muted-foreground/40")} />
                  </button>
                  {open ? (
                    <IntervalEditor value={intervals} onChange={(next) => setDay(day.key, next)} addLabel="Add window" />
                  ) : (
                    <p className="text-sm text-muted-foreground">No website slots are offered on {day.label.toLowerCase()}s.</p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
        <CardFooter className="justify-between">
          <p className="hidden max-w-2xl text-xs text-muted-foreground sm:block">Windows generate appointment start times using each service’s duration, buffers, lead time, and slot increment.</p>
          <Button onClick={saveSchedule} disabled={saving}>
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save weekly hours"}
          </Button>
        </CardFooter>
      </Card>

      <RangePublisher onPublish={publishRange} />

      <OverridesCard
        overrides={draft.overrides}
        restoringDate={restoringDate}
        onRestore={restoreWeeklyHours}
      />

      <Card>
        <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Time off</CardTitle>
            <CardDescription className="mt-1">Blocks availability without changing recurring hours or published date windows.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={addTimeOff}>
            <Plus className="h-3.5 w-3.5" /> Add time off
          </Button>
        </CardHeader>
        <CardContent>
          {draft.time_off.length === 0 ? (
            <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">No time off scheduled for {resource.name}.</div>
          ) : (
            <div className="flex flex-col gap-3">
              {draft.time_off.map((row, index) => (
                <div key={`${row.starts_at}-${index}`} className="grid gap-2 rounded-lg border bg-muted/15 p-3 lg:grid-cols-[1fr_1fr_1.4fr_auto] lg:items-end">
                  <Field label="Starts">
                    <Input
                      type="datetime-local"
                      value={isoToLocal(row.starts_at)}
                      onChange={(event) => patchTimeOff(index, { starts_at: localDateTimeToIso(event.target.value) })}
                    />
                  </Field>
                  <Field label="Ends">
                    <Input
                      type="datetime-local"
                      value={isoToLocal(row.ends_at)}
                      onChange={(event) => patchTimeOff(index, { ends_at: localDateTimeToIso(event.target.value) })}
                    />
                  </Field>
                  <Field label="Reason (optional)">
                    <Input value={row.reason ?? ""} placeholder="Conference, leave, personal…" onChange={(event) => patchTimeOff(index, { reason: event.target.value })} />
                  </Field>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove time-off range ${index + 1}`}
                    onClick={() => setDraft((current) => current ? { ...current, time_off: current.time_off.filter((_, rowIndex) => rowIndex !== index) } : current)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="justify-end">
          <Button onClick={saveSchedule} disabled={saving}>
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save schedule"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function SummaryCard({ icon, label, value, detail }: { icon: React.ReactNode; label: string; value: string; detail: string }): React.JSX.Element {
  return (
    <div className="rounded-lg border bg-card px-4 py-3 shadow-xs">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">{icon}{label}</div>
      <div className="mt-2 truncate text-base font-semibold">{value}</div>
      <div className="mt-0.5 truncate text-xs text-muted-foreground">{detail}</div>
    </div>
  );
}

function RangePublisher({ onPublish }: { onPublish: (input: BulkScheduleOverridesInput, dates: string[]) => Promise<boolean> }): React.JSX.Element {
  const today = useMemo(() => localDateKey(new Date()), []);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(() => addDateKey(today, 13));
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5, 6]);
  const [mode, setMode] = useState<"open" | "closed">("open");
  const [intervals, setIntervals] = useState<Interval[]>([["09:00", "17:00"]]);
  const [publishing, setPublishing] = useState(false);
  const dates = useMemo(() => matchingDateKeys(from, to, weekdays), [from, to, weekdays]);
  const rangeError = rangeValidation(from, to, weekdays, mode === "open" ? intervals : undefined);

  async function publish(): Promise<void> {
    if (rangeError) return;
    setPublishing(true);
    try {
      await onPublish({
        from,
        to,
        weekdays,
        ...(mode === "closed" ? { closed: true } : { hours: intervals })
      }, dates);
    } finally {
      setPublishing(false);
    }
  }

  return (
    <Card className="overflow-hidden border-primary/20">
      <div className="h-1 bg-primary" />
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><CalendarDays className="h-4 w-4" /></div>
          <div>
            <CardTitle>Publish a date range</CardTitle>
            <CardDescription className="mt-1">Choose days and working windows once. This creates date-specific availability that replaces recurring hours on those dates.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="From date"><Input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></Field>
          <Field label="To date"><Input type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} /></Field>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label>Apply on</Label>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setWeekdays([1, 2, 3, 4, 5])}>Weekdays</Button>
              <Button variant="ghost" size="sm" onClick={() => setWeekdays([0, 1, 2, 3, 4, 5, 6])}>Every day</Button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-7">
            {DAYS.map((day) => {
              const selected = weekdays.includes(day.weekday);
              return (
                <button
                  key={day.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setWeekdays((current) => selected ? current.filter((value) => value !== day.weekday) : [...current, day.weekday])}
                  className={cn(
                    "focus-ring flex h-9 items-center justify-center gap-1 rounded-md border text-xs font-medium transition-colors",
                    selected ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:border-border-strong"
                  )}
                >
                  {selected && <Check className="h-3 w-3" />}{day.short}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Action</Label>
          <div className="grid grid-cols-2 rounded-lg bg-muted p-1">
            <button type="button" onClick={() => setMode("open")} className={cn("focus-ring rounded-md px-3 py-2 text-sm font-medium transition-colors", mode === "open" ? "bg-card text-foreground shadow-xs" : "text-muted-foreground")}>Make available</button>
            <button type="button" onClick={() => setMode("closed")} className={cn("focus-ring rounded-md px-3 py-2 text-sm font-medium transition-colors", mode === "closed" ? "bg-card text-foreground shadow-xs" : "text-muted-foreground")}>Close dates</button>
          </div>
        </div>

        {mode === "open" && (
          <div className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-3">
            <Label>Bookable windows</Label>
            <IntervalEditor value={intervals} onChange={setIntervals} addLabel="Add another window" />
            <p className="text-xs text-muted-foreground">Create separate windows to leave a break—for example 09:30–13:00 and 14:00–17:30.</p>
          </div>
        )}

        <div className="rounded-lg border border-dashed bg-background px-3 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium">{dates.length} matching {dates.length === 1 ? "date" : "dates"}</div>
              <div className="text-xs text-muted-foreground">Preview before publishing</div>
            </div>
            <div className="flex max-w-full flex-wrap justify-end gap-1">
              {dates.slice(0, 6).map((date) => <Badge key={date} variant="outline">{formatShortDate(date)}</Badge>)}
              {dates.length > 6 && <Badge variant="secondary">+{dates.length - 6} more</Badge>}
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-between">
        <p className={cn("text-xs", rangeError ? "text-destructive" : "text-muted-foreground")}>
          {rangeError ?? "Publishing is immediate; existing appointments are not changed."}
        </p>
        <Button onClick={publish} disabled={publishing || Boolean(rangeError)}>
          {publishing ? "Publishing…" : mode === "closed" ? `Close ${dates.length} dates` : `Publish ${dates.length} dates`}
        </Button>
      </CardFooter>
    </Card>
  );
}

function OverridesCard({
  overrides,
  restoringDate,
  onRestore
}: {
  overrides: Schedule["overrides"];
  restoringDate: string | null;
  onRestore: (date: string) => Promise<void>;
}): React.JSX.Element {
  const entries = Object.entries(overrides).sort(([a], [b]) => a.localeCompare(b));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Published date overrides</CardTitle>
        <CardDescription>These dates replace the normal weekly pattern. Restore a date to make it follow recurring hours again.</CardDescription>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">No date-specific availability yet.</div>
        ) : (
          <div className="max-h-[440px] divide-y overflow-y-auto rounded-lg border">
            {entries.map(([date, intervals]) => (
              <div key={date} className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="w-28 shrink-0">
                    <div className="text-sm font-medium">{formatDateLabel(date)}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{date}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {intervals.length === 0 ? <Badge variant="secondary">Closed</Badge> : intervals.map(([start, end], index) => <Badge key={`${start}-${end}-${index}`} variant="outline">{start}–{end}</Badge>)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" disabled={restoringDate === date} onClick={() => onRestore(date)}>
                  {restoringDate === date ? "Restoring…" : "Restore weekly"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function IntervalEditor({ value, onChange, addLabel }: { value: Interval[]; onChange: (value: Interval[]) => void; addLabel: string }): React.JSX.Element {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {value.map((interval, index) => (
        <div key={index} className="flex items-center gap-1 rounded-md border bg-card p-1 shadow-xs">
          <Input
            type="time"
            aria-label={`Window ${index + 1} start`}
            value={interval[0]}
            onChange={(event) => {
              const next = value.map((row) => [...row] as Interval);
              next[index] = [event.target.value, interval[1]];
              onChange(next);
            }}
            className="h-7 w-[104px] border-0 bg-transparent px-2 shadow-none"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="time"
            aria-label={`Window ${index + 1} end`}
            value={interval[1]}
            onChange={(event) => {
              const next = value.map((row) => [...row] as Interval);
              next[index] = [interval[0], event.target.value];
              onChange(next);
            }}
            className="h-7 w-[104px] border-0 bg-transparent px-2 shadow-none"
          />
          <Button variant="ghost" size="icon-sm" className="h-7 w-7" aria-label={`Remove window ${index + 1}`} onClick={() => onChange(value.filter((_, rowIndex) => rowIndex !== index))}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...value, [...DEFAULT_WINDOW] as Interval])}>
        <Plus className="h-3.5 w-3.5" /> {addLabel}
      </Button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return <div className="flex min-w-0 flex-col gap-1.5"><Label>{label}</Label>{children}</div>;
}

function normalizeSchedule(schedule: Schedule): Schedule {
  return {
    ...schedule,
    weekly_hours: schedule.weekly_hours ?? {},
    overrides: schedule.overrides ?? {},
    time_off: (schedule.time_off ?? []).map((row) => {
      const legacy = row as typeof row & { from?: string; to?: string };
      return {
        starts_at: row.starts_at ?? legacy.from ?? "",
        ends_at: row.ends_at ?? legacy.to ?? "",
        ...(row.reason ? { reason: row.reason } : {})
      };
    })
  };
}

function scheduleStats(schedule: Schedule | null): { openDays: number; weeklyHours: number; overrideCount: number; nextOverride?: string } {
  if (!schedule) return { openDays: 0, weeklyHours: 0, overrideCount: 0 };
  const openDays = DAYS.filter((day) => (schedule.weekly_hours[day.key] ?? []).length > 0).length;
  const weeklyHours = DAYS.reduce((total, day) => total + intervalsMinutes(schedule.weekly_hours[day.key] ?? []) / 60, 0);
  const overrideDates = Object.keys(schedule.overrides).sort();
  const today = localDateKey(new Date());
  return {
    openDays,
    weeklyHours,
    overrideCount: overrideDates.length,
    nextOverride: overrideDates.find((date) => date >= today)
  };
}

function validateScheduleDraft(schedule: Schedule): string | null {
  for (const day of DAYS) {
    const error = intervalValidation(schedule.weekly_hours[day.key] ?? []);
    if (error) return `${day.label}: ${error}`;
  }
  for (const [date, intervals] of Object.entries(schedule.overrides)) {
    const error = intervalValidation(intervals);
    if (error) return `${date}: ${error}`;
  }
  for (const row of schedule.time_off) {
    if (!row.starts_at || !row.ends_at || Date.parse(row.starts_at) >= Date.parse(row.ends_at)) return "Every time-off range needs an end after its start.";
  }
  return null;
}

function rangeValidation(from: string, to: string, weekdays: number[], intervals?: Interval[]): string | null {
  if (!validDateKey(from) || !validDateKey(to)) return "Choose a valid start and end date.";
  if (to < from) return "The end date must be on or after the start date.";
  if (dateSpanDays(from, to) > 366) return "Choose a date range of 366 days or fewer.";
  if (weekdays.length === 0) return "Select at least one day of the week.";
  if (intervals && intervals.length === 0) return "Add at least one bookable window.";
  return intervals ? intervalValidation(intervals) : null;
}

function intervalValidation(intervals: Interval[]): string | null {
  const parsed = intervals.map(([start, end]) => [timeMinutes(start), timeMinutes(end)] as const);
  if (parsed.some(([start, end]) => start < 0 || end < 0 || start >= end)) return "Each window must end after it starts.";
  const ordered = [...parsed].sort((a, b) => a[0] - b[0]);
  if (ordered.some((interval, index) => index > 0 && interval[0] < ordered[index - 1]![1])) return "Bookable windows cannot overlap.";
  return null;
}

function intervalsMinutes(intervals: Interval[]): number {
  return intervals.reduce((total, [start, end]) => total + Math.max(0, timeMinutes(end) - timeMinutes(start)), 0);
}

function timeMinutes(value: string): number {
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value) && value !== "24:00") return -1;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

export function matchingDateKeys(from: string, to: string, weekdays: number[]): string[] {
  if (!validDateKey(from) || !validDateKey(to) || to < from || weekdays.length === 0) return [];
  const selected = new Set(weekdays);
  const start = Date.parse(`${from}T00:00:00.000Z`);
  const end = Date.parse(`${to}T00:00:00.000Z`);
  const dates: string[] = [];
  let scanned = 0;
  for (let cursor = start; cursor <= end && scanned < 367; cursor += 86_400_000, scanned += 1) {
    const date = new Date(cursor);
    if (selected.has(date.getUTCDay())) dates.push(date.toISOString().slice(0, 10));
  }
  return dates;
}

function dateSpanDays(from: string, to: string): number {
  return Math.floor((Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / 86_400_000) + 1;
}

function validDateKey(value: string): boolean {
  const timestamp = Date.parse(`${value}T00:00:00.000Z`);
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === value;
}

function localDateKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function addDateKey(date: string, days: number): string {
  const parsed = new Date(`${date}T12:00:00`);
  parsed.setDate(parsed.getDate() + days);
  return localDateKey(parsed);
}

function isoToLocal(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localDateTimeToIso(local: string): string {
  if (!local) return "";
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function formatDateLabel(date: string): string {
  return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${date}T00:00:00.000Z`));
}

function formatShortDate(date: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${date}T00:00:00.000Z`));
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
